<template>

  <v-container fluid class="pa-0">

    <div>
      <app-select v-model="selectedCloudProvider" :items="cloudProviders" :disabled="componentDisabled" class="truncatable" item-text="name"
        item-value="id" label="appSignCloudTab.cloudType" required @change="onSelectedCloudProviderChanged" use-item-slot>
        <template v-slot:item="{ item }">
          <img v-if="item.icon" :src="item.icon" class="icon-cloud-provider" alt="">
          <span>{{ locale === Languages.English ? item.nameEn : item.name }}</span>
        </template>
        <template v-slot:selection="{ item }">
          <div class="d-flex flex-nowrap align-center overflow-x-hidden">
            <img v-if="item.icon" :src="item.icon" class="icon-cloud-provider" alt="">
            <div class="overflow-ellipsis">{{ locale === Languages.English ? item.nameEn : item.name }}</div>
          </div>
        </template>
      </app-select>
    </div>

    <app-sign-cloud-provider-deposit-sign v-if="isDepositSignProviderRendered" v-show="isDepositSignProviderVisible"
      :certificateAuthority="certificateAuthority" :disabled="disabled" :eventBus="eventBus" :privateKeyType="privateKeyType"
      :signMethod="signMethod" :useUserCertificates="useUserCertificates" :should-validate-key-type="shouldValidateKeyType"
      @keyReaded="onSignKeyReaded" @ready="onReady" @not-ready="onNotReady">
    </app-sign-cloud-provider-deposit-sign>

    <app-sign-cloud-provider-diia v-if="isDiiaProviderRendered" v-show="isDiiaProviderVisible" :disabled="disabled" :eventBus="eventBus"
      :privateKeyType="privateKeyType" :signMethod="signMethod" :signProvider="CloudProvider.DPDiya"
      :should-validate-key-type="shouldValidateKeyType" @keyReaded="onSignKeyReaded" @ready="onReady" @not-ready="onNotReady">
    </app-sign-cloud-provider-diia>

    <app-sign-cloud-provider-cloud-key v-if="isCloudKeyProviderRendered" v-show="isCloudKeyProviderVisible"
      :certificateAuthority="certificateAuthority" :disabled="disabled" :eventBus="eventBus" :privateKeyType="privateKeyType"
      :signMethod="signMethod" :useUserCertificates="useUserCertificates" :should-validate-key-type="shouldValidateKeyType"
      @keyReaded="onSignKeyReaded" @ready="onReady" @not-ready="onNotReady">
    </app-sign-cloud-provider-cloud-key>

    <template v-for="p in providersWithDefaultComponent">
      <app-sign-cloud-provider-default v-if="isDynamicProviderRendered(p.id)" v-show="isDynamicProviderVisible(p.id)" :key="p.id"
        :disabled="disabled" :eventBus="eventBus" :privateKeyType="privateKeyType" :signMethod="signMethod" :signProvider="p.id"
        :should-validate-key-type="shouldValidateKeyType" @keyReaded="onSignKeyReaded" @ready="onReady" @not-ready="onNotReady" />
    </template>

  </v-container>

</template>

<script>
import AppSelect from '@/components/Common/AppSelect';
import { getLocale, getLocaleR } from '@/helpers/locale-helper';
import { actions as _act } from '@/models/store.actions';
import AppSignBaseTab from '@/sign/components/AppSignBaseTab';
import AppSignCloudProviderDepositSign from '@/sign/components/AppSignCloudProviderDepositSign';
import AppSignCloudProviderDiia from '@/sign/components/AppSignCloudProviderDiia';
import AppSignCloudProviderCloudKey from '@/sign/components/AppSignCloudProviderCloudKey';
import { CloudProvider, SignMethod } from '@/sign/models/sign-constants';
import AppSignCloudProviderDefault from '@/sign/components/AppSignCloudProviderDefault.vue';
import { appSettingsHelper } from '@/helpers/settings-helper';
import { CloudProviderSettings } from '@/sign/models/sign-settings.model';
import { Languages } from '@/models/enums';
import { messageService } from '@/helpers/message.service';
import DepositSignIcon from '@/assets/CloudProviderLogos/DepositSign.svg';
import CloudKeyIcon from '@/assets/CloudProviderLogos/CloudKey.svg';
import DPDiyaIcon from '@/assets/CloudProviderLogos/DPDiya.svg';
import PrivatSmartIdIcon from '@/assets/CloudProviderLogos/PrivatSmartId.svg';
import ESignIcon from '@/assets/CloudProviderLogos/ESign.svg';
import { logger } from '@/helpers/log.service';

export default {
  name: 'AppSignCloudTab',
  extends: AppSignBaseTab,
  components: {
    AppSelect,
    AppSignCloudProviderDepositSign,
    AppSignCloudProviderDiia,
    AppSignCloudProviderCloudKey,
    AppSignCloudProviderDefault
  },
  data() {
    return {
      signMethod: SignMethod.Cloud,
      cloudProviders: [],
      renderedProviders: [],
      selectedCloudProvider: null,
      providersWithCustomComponent: [`${CloudProvider.DepositSign}`, `${CloudProvider.CloudKey}`],
      providersIcon: {
        [CloudProvider.DepositSign]: DepositSignIcon,
        [CloudProvider.CloudKey]: CloudKeyIcon,
        [CloudProvider.DPDiya]: DPDiyaIcon,
        [CloudProvider.PrivatSmartId]: PrivatSmartIdIcon,
        [CloudProvider.ESign]: ESignIcon
      },
      iitCloudProviderSettings: appSettingsHelper.iitCloudProviderSettings
    };
  },
  computed: {
    locale() {
      return this.$store.getters[_act.SETT_GET_LOCALEGET];
    },
    Languages() {
      return Languages;
    },
    appSettings() {
      return this.$store.getters[_act.SETT_GET_APPSET];
    },
    componentDisabled() {
      return this.disabled || this.isSignKeyReaded;
    },
    isDepositSignProviderRendered() {
      return this.renderedProviders.includes(CloudProvider.DepositSign);
    },
    isDepositSignProviderVisible() {
      return this.selectedCloudProvider === CloudProvider.DepositSign;
    },
    isDiiaProviderRendered() {
      return this.renderedProviders.includes(CloudProvider.DPDiya);
    },
    isDiiaProviderVisible() {
      return this.selectedCloudProvider === CloudProvider.DPDiya;
    },
    isCloudKeyProviderRendered() {
      return this.renderedProviders.includes(CloudProvider.CloudKey);
    },
    isCloudKeyProviderVisible() {
      return this.selectedCloudProvider === CloudProvider.CloudKey;
    },
    // do not remove, overrides the parent's one
    signingParamsRequestChildMsg() {
      return `${this.signingParamsRequestMsg}-${this.selectedCloudProvider}`;
    },
    // do not remove, overrides the parent's one
    signingParamsResponseChildMsg() {
      return `${this.signingParamsResponseMsg}-${this.selectedCloudProvider}`;
    },
    CloudProvider() {
      return CloudProvider;
    },
    providersWithDefaultComponent() {
      return this.iitCloudProviderSettings.filter(p => !this.providersWithCustomComponent.includes(p.id) && p.enabled);
    }
  },
  created() {
    this.initSignCloudTab();
    this.subscribeCloudTab();
  },
  destroyed() {
    this.unsubscribeCloudTab();
  },
  methods: {
    appendCloudProviderToRendered(v) {
      if (!this.renderedProviders.includes(v)) {
        this.renderedProviders.push(v);
      }
    },
    isDynamicProviderRendered(provider) {
      return this.renderedProviders.includes(provider);
    },
    isDynamicProviderVisible(provider) {
      return this.selectedCloudProvider === provider;
    },
    async addCloudProviderWithCustomComponent(cloudProvider) {
      this.cloudProviders.push(new CloudProviderSettings(
        cloudProvider,
        getLocaleR(`appSignCloudTab.cloudProvider.${cloudProvider}`, Languages.Ukrainian).toString(),
        getLocaleR(`appSignCloudTab.cloudProvider.${cloudProvider}`, Languages.English).toString(),
        cloudProvider in this.providersIcon ? this.providersIcon[cloudProvider] : null
      ));
    },
    initSignCloudTab() {
      logger.debug(`[AppSignCloudTab:initSignCloudTab] iitCloudProviderSettings`, this.iitCloudProviderSettings);
      if (appSettingsHelper.isDepositSignEnabled ||
          this.iitCloudProviderSettings.some(s => s.id === CloudProvider.DepositSign && s.enabled)) {
        this.addCloudProviderWithCustomComponent(CloudProvider.DepositSign);
      }

      if (appSettingsHelper.isDiiaEnabled) {
        this.addCloudProviderWithCustomComponent(CloudProvider.DPDiya);
      }

      if (appSettingsHelper.isCloudKeyEnabled ||
          this.iitCloudProviderSettings.some(s => s.id === CloudProvider.CloudKey && s.enabled)) {
        this.addCloudProviderWithCustomComponent(CloudProvider.CloudKey);
      }

      logger.debug(`[AppSignCloudTab:initSignCloudTab] providersWithDefaultComponent`, this.providersWithDefaultComponent);

      for (let p of this.providersWithDefaultComponent) {
        this.cloudProviders.push(
          new CloudProviderSettings(p.id, p.name, p.nameEn, p.id in this.providersIcon ? this.providersIcon[p.id] : null));
      }

      let cloudInfo = this.getCloudProviderFromStore();
      if (cloudInfo) {
        this.selectedCloudProvider = cloudInfo.selectedCloud;
      }

      if (!this.selectedCloudProvider || !this.cloudProviders.some((p) => this.selectedCloudProvider === p.id)) {
        this.selectedCloudProvider = this.cloudProviders.length > 0 ? this.cloudProviders[0].id : null;
      }

      logger.debug(`[AppSignCloudTab:initSignCloudTab] selectedCloudProvider=${this.selectedCloudProvider}`);

      this.appendCloudProviderToRendered(this.selectedCloudProvider);
    },
    onSelectedCloudProviderChanged(v) {
      this.appendCloudProviderToRendered(v);
      this.saveCloudProviderToStore(v);
      this.emitSignMethodChanged();
    },
    subscribeCloudTab() {
      this.eventBus.$on('app-sign-method-changed', this.onSignMethodChanged);
    },
    unsubscribeCloudTab() {
      this.eventBus.$off('app-sign-method-changed', this.onSignMethodChanged);
    },
    onSignMethodChanged(prms) {
      if (prms.signMethod === SignMethod.Cloud && !('signProvider' in prms)) {
        this.emitSignMethodChanged();
      }
    },
    emitSignMethodChanged() {
      this.eventBus.$emit('app-sign-method-changed', {
        signProvider: this.selectedCloudProvider,
        signMethod: this.signMethod
      });
    },
    getCloudProviderFromStore() {
      return this.isSeal ? this.$store.getters[_act.SIGN_GET_CLOUD_SEAL] : this.$store.getters[_act.SIGN_GET_CLOUD];
    },
    saveCloudProviderToStore(v) {
      let act = this.isSeal ? _act.SIGN_MUT_CLOUD_SEAL : _act.SIGN_MUT_CLOUD;
      this.$store.commit(act, { selectedCloud: v });
    },
    // do not remove, overrides the parent's one
    async onSigningParamsRequest() {
      let prms = null;
      if (this.selectedCloudProvider) {
        prms = await this.getSigningParams();
      } else {
        messageService.error({
          message: getLocale('appSignCloudTab.selectCloudType').toString()
        });
      }
      this.eventBus.$emit(this.signingParamsResponseMsg, prms);
    }
  }
};
</script>
<style lang="scss">
.icon-cloud-provider {
  height: 30px;
  max-width: 150px;
  margin-right: 15px;
}
</style>
